<template>
  <div class="growth-wizard getting-started-wizard">
    <template v-if="showCompletionScreen">
      <GrowthWizardCompletionScreen
        :organization="organization"
        :completion-screen-data="completionScreen.data"
        :completion-screen-template="completionScreen.template"
      />
    </template>
    <template v-else-if="showReplaceGsWelcomeScreen">
      <div
        id="growth-wizard-welcome-screen"
        class="replace-gs-welcome-screen"
      >
        <img
          :src="HUBSTAFF_LOGO"
          alt="Hubstaff"
          class="logo"
          width="80"
          height="80"
        />
        <div
          class="title"
          data-testid="replace-gs-welcome-title"
        >
          Manage your team without micromanaging.
        </div>
        <div class="subtitle">
          In just a few minutes, you'll have clear visibility into your team's work — without constant check-ins.
          Built for teams with <strong>virtual assistants</strong>, <strong>contractors</strong>, and <strong>remote employees</strong>.
        </div>
        <img
          :src="REPLACE_INITIAL_GS_STEPS_WELCOME_IMG"
          alt="Manage your team without micromanaging"
          class="welcome-image"
        />
        <AppButton
          type="primary"
          class="continue-button"
          data-testid="replace-gs-welcome-button"
          @click="hideWelcomeScreen"
        >
          Get started
        </AppButton>
      </div>
    </template>
    <template v-else>
      <div
        id="growth-wizard-section-steps"
        class="growth-wizard-section"
      >
        <div id="growth-wizard-header">
          <p class="title">👋 Let's get you started with Hubstaff!</p>
        </div>
        <div class="gs-steps wizard-progress-bar">
          <div class="progress-bar-item">
            <div
              class="progress-fill"
              :style="{ width: `${overallProgressPercentage}%` }"
            ></div>
          </div>
        </div>
      </div>
      <div
        v-loading="loading"
        class="loading static-curtain"
      />
      <div
        v-if="substepData"
        id="growth-wizard-section-substeps"
        class="growth-wizard-section"
      >
        <component
          :is="currentSubstepComponent"
          class="substep-component"
          v-bind="getSubstepProps()"
          v-on="getSubstepEvents()"
        />
      </div>

      <div
        v-if="substepData"
        class="streamlined-bottom-bar"
      >
        <div class="cta-wrapper">
          <div class="ctas-left">
            <AppButton
              v-if="showExploreOnMyOwnButton"
              class="hs-btn-outline-gray"
              @click="exploreOnMyOwn"
            >
              <i class="hi hi-16 hi-exit"></i>
              Explore on my own
            </AppButton>
          </div>

          <div class="ctas-right">
            <AppButton
              v-if="!isFirstSubstep"
              class="back-button hs-btn-outline-gray"
              @click="returnToPreviousStep"
            >
              <i class="hi hi-16 hi-left-arrow"></i>
              Back
            </AppButton>

            <AppButton
              v-if="allowSkipToNextStep"
              class="skip-button hs-btn-outline-gray"
              @click="skipToNextStep"
            >
              I'll do this later
            </AppButton>

            <AppButton
              v-tooltip="showContinueTooltip ? { placement: 'top' } : false"
              :title="continueTooltip"
              type="primary"
              class="continue-button"
              data-testid="continue-to-next-step"
              :disabled="!nextStepAllowed"
              @click="continueToNextStep($event)"
            >
              {{ nextStepText }}
              <i
                v-if="!isLastSubstep"
                class="hi hi-16 hi-right-arrow"
              ></i>
            </AppButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { HUBSTAFF_LOGO, REPLACE_INITIAL_GS_STEPS_WELCOME_IMG } from '~/rails_assets';
import { GettingStartedResource } from '~/resources';
import { SUBSTEP_COMPONENTS } from '~/components/growth_wizard/getting_started/substeps/mappings';
import WizardBaseMixin from '~/mixins/GrowthWizard/WizardBaseMixin';
import GrowthWizardCompletionScreen from '~/components/growth_wizard/completion_screens/GrowthWizardCompletionScreen.vue';

export default {
  name: 'GettingStartedIndex',
  components: {
    GrowthWizardCompletionScreen,
  },
  mixins: [WizardBaseMixin],
  props: {
    replaceInitialGsSteps: {
      type: Boolean,
      default: false,
    },
    userGettingStartedV2: {
      type: Boolean,
      default: false,
    },
    trackTimeImmediately: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      HUBSTAFF_LOGO,
      REPLACE_INITIAL_GS_STEPS_WELCOME_IMG,
      substepsWithFinishedHandlers: [
        'set_up_project',
        'add_a_holiday',
        'set_up_timesheet_approvals',
        'set_up_time_limits',
      ],
      isSidebarCollapsed: false,
      sidebarObserver: null,
      userDismissedWelcome: false,
    };
  },
  computed: {
    substepComponentsMap() {
      return SUBSTEP_COMPONENTS;
    },
    resourceAPI() {
      return GettingStartedResource;
    },
    anySubstepCompleted() {
      return this.allSubsteps.some((substep) => substep.status === 'completed');
    },
    showExploreOnMyOwnButton() {
      return this.isSidebarCollapsed && !this.userGettingStartedV2 && !this.trackTimeImmediately;
    },
    showReplaceGsWelcomeScreen() {
      return this.replaceInitialGsSteps && !this.userDismissedWelcome && this.isFirstSubstep && !this.anySubstepCompleted;
    },
    isLastSubstep() {
      return !this.nextSubstep;
    },
    nextStepText() {
      return this.nextSubstep ? 'Continue' : 'Complete onboarding';
    },
  },
  mounted() {
    this.setupSidebarObserver();
  },
  beforeUnmount() {
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
  },
  beforeMount() {
    this.getSubstepData();
  },
  methods: {
    hideWelcomeScreen() {
      this.userDismissedWelcome = true;
    },
    setupSidebarObserver() {
      const sidebarEl = document.querySelector('.hotwired-sidebar');

      if (sidebarEl) {
        this.isSidebarCollapsed = sidebarEl.getAttribute('data-collapsed') === 'true';

        this.sidebarObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-collapsed') {
              this.isSidebarCollapsed = mutation.target.getAttribute('data-collapsed') === 'true';
            }
          }
        });

        this.sidebarObserver.observe(sidebarEl, { attributes: true, attributeFilter: ['data-collapsed'] });
      }
    },
  },
};
</script>
